/**
 * 待激活卡片组件
 * 用于显示未激活状态的卡片（审核中、寄出中、待激活等）
 */

import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import CardView from '../card-view'
import CustomButton from '@/components/CustomButton'
import SkeletonBox from '@/components/SkeletonBox'

export interface PendingCardProps {
    /** 卡片名称 */
    cardName?: string
    /** 物流单号 */
    trackingNumber?: string
    /** 物流公司名称 */
    courierName?: string
    /** 激活按钮点击回调（跳转到激活页面） */
    onActivate?: () => void
    /** 激活按钮是否可用（由父组件控制，通常在 pending_activation 状态时可用） */
    canActivate?: boolean
    /** 自定义类名 */
    className?: string
    /** 是否隐藏卡片预览（用于在swiper中展示时隐藏，避免重复显示卡片） */
    hideCardPreview?: boolean
    /** 是否正在加载详情（用于显示骨架屏） */
    loading?: boolean
    /** 查询kyc审核状态按钮点击回调（可选） */
    onAccelerate?: () => void
    /** 查询kyc审核状态按钮是否可用（可选） */
    canAccelerate?: boolean
    /** 查询kyc审核状态按钮是否处于 loading 状态（可选） */
    accelerateLoading?: boolean
}

const PendingCard: React.FC<PendingCardProps> = ({
    trackingNumber,
    courierName,
    onActivate,
    canActivate = false,
    className = '',
    hideCardPreview = false,
    loading = false,
    onAccelerate,
    canAccelerate = false,
    accelerateLoading = false,
}) => {
    const { t } = useTranslation()

    // 复制邮寄单号
    const handleCopyTrackingNumber = useCallback(async () => {
        if (!trackingNumber) return

        try {
            await navigator.clipboard.writeText(trackingNumber)
            Toast.success(t('复制成功'))
        } catch (error) {
            console.error('复制失败:', error)
            Toast.fail(t('复制失败'))
        }
    }, [trackingNumber, t])

    // 是否有物流信息
    const hasShippingInfo = !!(trackingNumber || courierName)

    // 骨架屏
    if (loading) {
        return (
            <div className={`${className}`}>
                {/* 卡片预览（可选隐藏） */}
                {!hideCardPreview && (
                    <CardView cardStatus="pending" />
                )}

                {/* 状态卡片骨架 */}
                <div className="mx-5 bg-[#F9F9F9] rounded-2xl p-5 shadow-sm mt-3">
                    <SkeletonBox className="h-5 w-24 rounded mb-2" />
                    <SkeletonBox className="h-4 w-48 rounded mb-5" />
                    {/* 进度条骨架 */}
                    <div className="flex gap-1 mb-5">
                        <SkeletonBox className="h-1 flex-1 rounded-full" />
                        <SkeletonBox className="h-1 flex-1 rounded-full" />
                    </div>
                    <SkeletonBox className="h-4 w-40 rounded" />
                </div>

                {/* 按钮骨架 */}
                <div className="mx-5 mt-6">
                    <SkeletonBox className="h-12 w-full rounded-full" />
                </div>
            </div>
        )
    }

    return (
        <div className={`${className}`}>
            {/* 卡片预览（可选隐藏） */}
            {!hideCardPreview && (
                <CardView cardStatus="pending" />
            )}

            {/* 卡片名称 */}
            {/* <div className={`text-center text-lg font-semibold text-[#1D2129] ${hideCardPreview ? 'mt-2' : 'mt-6'} mb-4`}>
                {displayCardName}
            </div> */}

            {/* 状态卡片 */}
            <div className="mx-5 bg-[#F9F9F9] rounded-2xl p-5 shadow-sm mt-3">
                {/* 状态标题 */}
                <div className="flex items-center gap-2 mb-2">
                    <img src="/images/card-v2/card-finish.svg" alt="finish" />
                    <span className="text-base font-medium text-[#1D2129]">{t('申请已提交')}</span>
                </div>

                {/* 状态描述 */}
                <div className="text-sm text-[#86909C] mb-5">
                    {t('通常这个过程会在7个工作日内完成')}
                </div>

                {/* 进度条：审核 -> 启用 */}
                <div className="flex mb-5">
                    {/* 审核（始终高亮） */}
                    <div className="flex-1 mr-1">
                        <div className="h-1 relative mb-2">
                            <div className="absolute inset-0 rounded-full bg-[var(--primary-color)]" />
                        </div>
                        <div className="text-sm text-[#667085]">{t('审核')}</div>
                    </div>
                    {/* 启用（等待用户手动激活） */}
                    <div className="flex-1">
                        <div className="h-1 relative mb-2">
                            <div className="absolute inset-0 rounded-full bg-[#E5E6EB]" />
                        </div>
                        <div className="text-sm text-[#667085]">{t('启用')}</div>
                    </div>
                </div>

                {/* 物流信息 */}
                <div className="text-sm">
                    <span className="text-[#1D2129]">{t('物流信息')}</span>
                    {hasShippingInfo ? (
                        <>
                            <span className="text-[#86909C] ml-3 break-all">
                                {courierName} {trackingNumber}
                            </span>
                            {trackingNumber && (
                                <img
                                    className="inline-block cursor-pointer ml-2 align-middle"
                                    src="/images/card-v2/copy.svg"
                                    alt="copy"
                                    onClick={handleCopyTrackingNumber}
                                />
                            )}
                        </>
                    ) : (
                        <span className="text-[#86909C] ml-3">{t('暂无物流信息')}</span>
                    )}
                </div>

                {/* 查询kyc审核状态按钮 */}
                {onAccelerate && (
                    <div className="mt-4">
                        <CustomButton

                            onClick={onAccelerate}
                            disabled={!canAccelerate}
                            loading={accelerateLoading}
                        >
                            {t('查询kyc审核状态')}
                        </CustomButton>
                    </div>
                )}
            </div>

            {/* 操作按钮 */}
            <div className="mx-5 mt-6 space-y-3">
                {onActivate && (
                    <CustomButton
                        onClick={onActivate}
                        disabled={!canActivate}
                    >
                        {t('激活卡片')}
                    </CustomButton>
                )}
            </div>
        </div>
    )
}

export default PendingCard

